/* ---------------------------------------------------------------------------------------------------
   WISHLIST (Wishlist Plus / Swym)
   ---------------------------------------------------------------------------------------------------
   Glue between the theme-rendered buttons in snippets/wishlist-button.liquid and Swym's storefront SDK.

   Swym does the real work — persisting items, the wishlist drawer, cross-device sync. This file only:
     1. flags readiness so the header counter can stop hiding,
     2. keeps that counter in sync,
     3. re-binds buttons after the theme swaps DOM that Swym has never seen,
     4. mirrors saved state onto `aria-pressed`.

   IMPORTANT: none of this runs until the Swym app embed is enabled in the theme editor. Without it
   the SDK never arrives, so everything below stays inert — which is the desired failure mode: the
   buttons render and simply do nothing rather than erroring.

   On the SDK handle: Swym's docs say to push into `window.SwymCallbacks` and receive `swat`. This
   store's build (v7) never drains that array — callbacks sat queued for a whole session — and exposes
   the SDK as `window._swat` instead. Both paths are wired; whichever arrives first wins.
   --------------------------------------------------------------------------------------------------- */

(function () {
  'use strict';

  /* -------------------------------------------------------------------------------------------------
     The wishlist is a page; Swym's drawer is retired
     -------------------------------------------------------------------------------------------------
     Every Swym control that would open the slide-out — the View button on the "added" toast, the
     "View Wishlist" tooltip on a heart, anything linking to #swym-list — is sent to the wishlist page
     instead, and assets/wishlist.css removes the drawer itself behind the class set here, so nothing
     Swym does can slide it out. The header heart is a plain link to the same page
     (sections/header.liquid).

     The URL is read off this script tag (`data-wishlist-page`, set by layout/theme.liquid). It is only
     present when the store has the page, so its absence leaves Swym's drawer untouched rather than
     redirecting into a 404. Read it first thing: document.currentScript is only meaningful while this
     file is executing. */

  const WISHLIST_PAGE_URL = (document.currentScript && document.currentScript.dataset.wishlistPage) || '';
  const DRAWER_OPENERS = [
    '.swym-storefront-layout-notification-action',
    '.swym-open-ui',
    'a[href="#swym-list"]',
    'a[href="#swym-wishlist"]'
  ].join(', ');

  /* Never redirect FROM the wishlist page. Swym's page routes its own tabs and collection views
     through the same #swym-list hashes (its script sets location.hash = StorefrontLayoutUrls.List and
     reads it back), so on the page both redirects below would reload the page out from under Swym —
     mid-interaction, e.g. right after an add-to-cart. The drawer stays hidden there regardless. */
  const ON_WISHLIST_PAGE =
    WISHLIST_PAGE_URL &&
    window.location.pathname.replace(/\/$/, '') === new URL(WISHLIST_PAGE_URL, window.location.origin).pathname.replace(/\/$/, '');

  if (WISHLIST_PAGE_URL) {
    document.documentElement.classList.add('wishlist-page-mode');
  }

  if (WISHLIST_PAGE_URL && !ON_WISHLIST_PAGE) {
    document.addEventListener(
      'click',
      function (event) {
        const opener = event.target.closest && event.target.closest(DRAWER_OPENERS);

        if (!opener) return;

        /* Capture phase on the document runs before Swym's own handler wherever it is bound, so
           stopping propagation means the drawer never opens; preventDefault covers the anchors. */
        event.preventDefault();
        event.stopPropagation();
        window.location.assign(WISHLIST_PAGE_URL);
      },
      true
    );

    /* Swym also opens the drawer from the URL — its own controls set #swym-list, and that hash can
       arrive in a shared link. Send those to the page as well. */
    function redirectDrawerHash() {
      if (window.location.hash === '#swym-list' || window.location.hash === '#swym-wishlist') {
        window.location.replace(WISHLIST_PAGE_URL);
      }
    }

    window.addEventListener('hashchange', redirectDrawerHash);
    redirectDrawerHash();
  }

  /* Swym binds `swym-button` elements once at init, so anything the theme re-renders afterwards
     arrives unbound. Re-scan from `body`: a single, unambiguous selector. A comma-separated list of
     the specific containers would be tighter, but Swym's `initializeActionButtons` is documented only
     with one selector and re-binding is rare enough that the wider scan costs nothing. */
  const REBIND_SCOPE = 'body';

  /* Theme events that mean "product cards or a product form just got replaced".
     `facet:update` is collection filtering and sorting (assets/theme.js, facets-form).
     `dialog:after-show` covers the quick-buy modal, which fetches its product markup on open. */
  const REBIND_EVENTS = ['facet:update', 'dialog:after-show', 'cart-drawer:refreshed', 'shopify:section:load'];

  function sdk() {
    return window._swat || window.swat || null;
  }

  let initialised = false;

  function init(swat) {
    if (initialised || !swat) return;

    initialised = true;
    document.documentElement.classList.add('swym-ready');

    initHeaderCounter(swat);
    initRebinding(swat);
    initStateSync();
  }

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(init);

  /* The documented path above is dead on this build, so also watch for `_swat` directly. Poll rather
     than assume load order: the embed is injected via content_for_header and its scripts load
     asynchronously, so the SDK can land well after this file runs. */
  (function waitForSdk(attempt) {
    if (initialised) return;

    const swat = sdk();

    if (swat && typeof swat.initializeActionButtons === 'function') return init(swat);

    if (attempt < 120) {
      setTimeout(function () {
        waitForSdk(attempt + 1);
      }, 250);
    }
  })(0);

  /* -------------------------------------------------------------------------------------------------
     Header counter
     ------------------------------------------------------------------------------------------------- */

  function initHeaderCounter(swat) {
    const counters = document.querySelectorAll('.swym-wishlist-header-counter');

    if (!counters.length) return;

    let count = 0;

    function render() {
      counters.forEach(function (node) {
        /* Empty rather than "0": assets/wishlist.css only reveals the dot when it has content, so an
           empty string is what hides it for a customer with nothing saved. The number itself is not
           displayed — the indicator is a dot, to match the cart's — but it is what the CSS keys on. */
        node.textContent = count > 0 ? String(count) : '';
      });
    }

    /* Source of truth on load; the events below keep it current without re-fetching on every toggle. */
    swat.fetch(function (products) {
      count = products ? products.length : 0;
      render();
    });

    /* The wishlist page counts the items Swym has actually rendered (assets/wishlist-page.js). That is
       the only signal for "Clear list", which empties the list without firing a removal event. */
    document.addEventListener('wishlist:count', function (event) {
      count = event.detail.count;
      render();
    });

    if (!swat.evtLayer) return;

    swat.evtLayer.addEventListener('sw:addedtowishlist', function () {
      count += 1;
      render();
    });

    swat.evtLayer.addEventListener('sw:removedfromwishlist', function () {
      count = Math.max(0, count - 1);
      render();
    });
  }

  /* -------------------------------------------------------------------------------------------------
     Re-binding after the theme replaces DOM
     ------------------------------------------------------------------------------------------------- */

  function initRebinding(swat) {
    if (typeof swat.initializeActionButtons !== 'function') return;

    let pending = null;

    /* Which heart ELEMENTS are bound — tracked by node identity, not by Swym's `swym-loaded` class.
       The class is unreliable both ways: fresh server markup arrives without it, and re-parsed markup
       (innerHTML set from existing HTML) keeps the class string while losing the listener. Worse,
       `swym-loaded` is also Swym's own guard, so a re-parsed heart would be skipped by
       initializeActionButtons too. A WeakSet of nodes sidesteps all of that. Seeded with whatever
       Swym had already bound by the time the SDK reached us. */
    const bound = new WeakSet();

    document.querySelectorAll('.wishlist-button.swym-loaded').forEach(function (heart) {
      bound.add(heart);
    });

    /* Coalesce to one re-scan per tick: several of these events can fire together (a facet change
       reloads the grid and the section), and the observer below fires per mutation record. A timer,
       not requestAnimationFrame — rAF is throttled to nothing in a background tab, and a re-bind that
       waits for a paint can leave hearts dead until the shopper happens to scroll. */
    function rebind() {
      if (pending) return;

      pending = setTimeout(function () {
        pending = null;

        const fresh = Array.prototype.filter.call(document.querySelectorAll('.wishlist-button'), function (heart) {
          return !bound.has(heart);
        });

        if (!fresh.length) return;

        fresh.forEach(function (heart) {
          /* Strip the stale stamp so Swym's guard lets it bind, and mark it ours before the call —
             Swym stamps asynchronously, and re-processing a node mid-flight would bind it twice. */
          heart.classList.remove('swym-loaded');
          bound.add(heart);
        });

        swat.initializeActionButtons(REBIND_SCOPE);
      }, 0);
    }

    REBIND_EVENTS.forEach(function (name) {
      document.addEventListener(name, rebind);
    });

    /* The events cover the theme's own re-renders. Apps re-render too, and announce nothing: Essential
       Preorder swaps the sold-out button on every card it covers, and Swym binds `swym-button`
       elements once at init and never watches for more — so every heart recreated by that swap was
       dead, which is exactly "I can't wishlist sold-out items" on the A/B variant where those buttons
       show. Watch for unbound hearts directly rather than enumerate who might re-render what. */
    new MutationObserver(rebind).observe(document.body, { childList: true, subtree: true });

    /* And anything already dead by the time the SDK arrived. */
    rebind();
  }

  /* -------------------------------------------------------------------------------------------------
     Sold-out item: saving also opens the restock form
     -------------------------------------------------------------------------------------------------
     Saving a sold-out item and asking to hear when it returns are the same intent, so one tap does
     both: Swym stores it, then Essential Preorder's back-in-stock popup opens for the email. This
     applies wherever the heart appears — product cards as well as the product page.

     Bound at module level rather than inside the Swym callback on purpose. The restock form is the
     half that actually recovers the sale, and it must still open if Swym is misconfigured or slow —
     which is exactly the state the theme is in until the app embed is switched on. */

  const BIS_TRIGGER = '[data-essential-preorder-element="back-in-stock-button"]';
  const BIS_WAIT_MS = 2000;

  document.addEventListener(
    'click',
    function (event) {
      const button = event.target.closest && event.target.closest('.wishlist-button');

      /* Capture phase, so this reads the state Swym is about to change. An already-saved item means
         the click removes it, and removing is not a request to be notified. */
      if (!button || isAdded(button)) return;

      /* Only a genuinely unavailable product gets the restock form, and only Liquid knows that —
         see data-available in snippets/wishlist-button.liquid. Essential renders its trigger on a
         low-stock threshold rather than zero stock, so a buyable product can carry one and offering
         to notify a shopper about something they can add to cart right now reads as broken. */
      if (button.dataset.available !== 'false') return;

      const trigger = restockTriggerFor(button);

      if (trigger) openRestockFormAfterSave(trigger);
    },
    true
  );

  /* Finding WHICH trigger, once data-available has established that a restock form is wanted at all.

     A trigger's presence is not a sold-out test — Essential renders one on a low-stock threshold, so
     buyable products carry them too. That is what data-available is for; this function only picks the
     right element.

     A page-wide lookup is still wrong: a PDP routinely carries other products' triggers in "goes well
     with", related products and recently viewed, and querySelector returns whichever comes first in
     the DOM — which once opened the restock form for an unrelated upsell, under that upsell's name.

     Scope to the product the heart belongs to. `.product` is the PDP's own gallery + info wrapper and
     excludes every card section below it. The surrounding <product-rerender> is id'd
     `product-info-<productId>-<template>`, which lets us confirm the scope really is this product
     before trusting anything inside it. No match means no popup — staying silent beats asking a
     shopper to be notified about something they were not looking at. */
  function restockTriggerFor(button) {
    /* A card is the tightest product scope there is, and it is checked first because a card can
       itself sit inside a PDP's upsells — scoping to `.product` there would reach past the card and
       pick up the main product's trigger. */
    const card = button.closest('product-card, .product-card');

    if (card) return card.querySelector(BIS_TRIGGER);

    const scope = button.closest('.product') || button.closest('product-rerender');

    if (!scope) return null;

    const productId = button.dataset.productId;
    const rerender = button.closest('product-rerender');

    if (productId && rerender && rerender.id && rerender.id.indexOf(productId) === -1) return null;

    return scope.querySelector(BIS_TRIGGER);
  }

  function openRestockFormAfterSave(trigger) {
    const swat = sdk();
    let opened = false;

    function open() {
      if (opened) return;

      opened = true;
      clearTimeout(timer);

      if (swat && swat.evtLayer) swat.evtLayer.removeEventListener('sw:addedtowishlist', open);

      trigger.click();
    }

    /* Prefer Swym's own confirmation so the popup follows a real save; fall back on a timer so a
       failed or slow save still surfaces the form rather than swallowing the interaction. */
    const timer = setTimeout(open, BIS_WAIT_MS);

    if (swat && swat.evtLayer) swat.evtLayer.addEventListener('sw:addedtowishlist', open);
  }

  /* -------------------------------------------------------------------------------------------------
     Wishlist page: adding to cart opens the theme's cart drawer
     -------------------------------------------------------------------------------------------------
     Swym's wishlist page renders its own add-to-cart buttons and posts the add itself, then leaves the
     shopper on the page with no feedback — the item is in the cart but nothing on screen says so. Hand
     them back to the theme's cart drawer, which is what every other add-to-cart on the site does.

     Swym announces nothing we can depend on — the only event alongside its add is
     `_add_to_cartEventFired`, from an analytics app — but it performs the add over XMLHttpRequest to
     /cart/add.js (fetch is covered too, in case a release switches). Watching that request is exact:
     the drawer opens the instant an add succeeds, whether from the button directly or from Swym's
     variant picker seconds later, and never opens when nothing was added. The earlier version polled
     the cart count after a click and opened the drawer anyway at a 5s deadline; for a click that added
     nothing (sold out, picker still open) that surfaced an unchanged cart late — or behind Swym's
     picker, which sits at z-index 2147483647 — and read as "the drawer does not open".

     Scoped to the wishlist page: the theme's own product forms open the drawer themselves. */

  let cartRevealedAt = 0;

  function revealCartDrawer() {
    const drawer = document.getElementById('cart-drawer');

    if (!drawer || typeof drawer.show !== 'function') return;

    /* Both transports are hooked; one add must not reveal twice. */
    if (Date.now() - cartRevealedAt < 1000) return;

    cartRevealedAt = Date.now();
    document.dispatchEvent(new CustomEvent('cart:refresh'));

    /* The theme's dialog hides in two steps: hide() drops `open` at once, and display:none lands only
       when the leave animation finishes. A show() in that gap sets `open` and display:block, then the
       pending hide resolves and sets display:none — leaving a drawer that is "open" and invisible.
       That gap is exactly where an add-right-after-closing lands, so wait it out. */
    const midHide = !drawer.hasAttribute('open') && drawer.style.display === 'block';

    if (!midHide) {
      drawer.show();
      return;
    }

    let shown = false;

    function showOnce() {
      if (shown) return;

      shown = true;
      drawer.removeEventListener('dialog:after-hide', showOnce);
      drawer.show();
    }

    drawer.addEventListener('dialog:after-hide', showOnce);
    setTimeout(showOnce, 1000);
  }

  function isCartAdd(url) {
    return /\/cart\/add(\.js)?(\?|$)/.test(String(url || ''));
  }

  if (ON_WISHLIST_PAGE) {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.wishlistCartAdd = isCartAdd(url);

      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      const request = this;

      if (request.wishlistCartAdd) {
        request.addEventListener('load', function () {
          if (request.status >= 200 && request.status < 300) revealCartDrawer();
        });
      }

      return originalSend.apply(this, arguments);
    };

    const originalFetch = window.fetch;

    window.fetch = function (input) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const promise = originalFetch.apply(this, arguments);

      if (isCartAdd(url)) {
        promise
          .then(function (response) {
            if (response.ok) revealCartDrawer();
          })
          .catch(function () {});
      }

      return promise;
    };
  }

  /* -------------------------------------------------------------------------------------------------
     Accessibility state
     -------------------------------------------------------------------------------------------------
     Swym signals "saved" by toggling a class on the button. Screen readers need that on `aria-pressed`,
     and the label should change with it. Watching the class attribute keeps us correct regardless of
     which class name Swym's build settles on, and regardless of it being set outside our own handlers
     (e.g. state restored on load, or an item removed from the wishlist drawer). */

  const ADDED_CLASSES = ['swym-added', 'swym-added-custom'];

  function isAdded(button) {
    return ADDED_CLASSES.some(function (name) {
      return button.classList.contains(name);
    });
  }

  function initStateSync() {
    function sync(button) {
      const added = isAdded(button);
      /* Labels come off the button so they stay in the page's own language — this theme serves 15+
         markets and a hardcoded English fallback would surface on every non-English storefront. */
      const label = added ? button.dataset.labelAdded : button.dataset.labelAdd;

      button.setAttribute('aria-pressed', added ? 'true' : 'false');

      if (label) button.setAttribute('aria-label', label);
    }

    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        sync(mutation.target);
      });
    });

    function observe(button) {
      if (button.dataset.wishlistObserved) return;

      button.dataset.wishlistObserved = 'true';
      sync(button);
      observer.observe(button, { attributes: true, attributeFilter: ['class'] });
    }

    document.querySelectorAll('.wishlist-button').forEach(observe);

    /* Buttons that arrive with re-rendered sections still need observing. This fires on every DOM
       mutation anywhere on the page — carousels and the cart drawer are busy — so coalesce to one
       query per frame rather than one per mutation record. */
    let scanning = false;

    new MutationObserver(function () {
      if (scanning) return;

      scanning = true;
      requestAnimationFrame(function () {
        scanning = false;
        document.querySelectorAll('.wishlist-button:not([data-wishlist-observed])').forEach(observe);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
